/*
 * The typed edge of the API, for the Vieques Worx screens.
 *
 * One file that knows the route shapes, so a component never builds a URL by
 * hand and a renamed endpoint is one diff. lib/api.ts stays untouched — it is
 * core, shared by every Studio42 app, and "Worx needs a PUT helper" is not a
 * reason to change what every other project imports.
 */
import { api, get, post } from '../lib/api'
import { getLocale } from '../lib/i18n'

const put = <T>(path: string, data?: unknown) =>
  api<T>(path, { method: 'PUT', ...(data === undefined ? {} : { body: JSON.stringify(data) }) })

const remove = <T>(path: string) => api<T>(path, { method: 'DELETE' })

/**
 * Where an image actually lives.
 *
 * `logo_id` and `photo_id` normally hold an uploads id, served by the Worker
 * out of R2 at /api/files/<id>. A value that already starts with `/` is taken
 * as a path and used as-is, which is what lets seeded demo businesses carry
 * real artwork out of public/demo/ without inventing an R2 object for each
 * one. One branch, and it cannot collide: an uploads id is base64url and never
 * begins with a slash.
 */
export function imageUrl(id: string | null | undefined): string | null {
  if (!id) return null
  return id.startsWith('/') ? id : `/api/files/${id}`
}

/* ── Shapes ───────────────────────────────────────────────────────────────── */

export interface Category {
  id: string
  en: string
  es: string
  /** For chips and filters, where the long name does not fit. */
  shortEn: string
  shortEs: string
  sort: number
  active: boolean
}

/** A text in the reader's language, when the author's differs. `text` holds only the fields that were translated. */
export interface Translation {
  from: 'en' | 'es-PR'
  text: Record<string, string | undefined>
}

export interface JobCard {
  titleTranslated?: string | null
  isDemo?: boolean
  canApply?: boolean
  id: string
  title: string
  categoryId: string | null
  payMin: number | null
  payMax: number | null
  payUnit: string | null
  arrangement: string | null
  location: string | null
  schedule: string | null
  status: string
  publishedAt: number | null
  closesOn: string | null
  employerId: string
  employerName: string
  employerLogoId: string | null
  /** The business's one line, shown in the corner of every card. */
  employerTagline: string | null
  /** Present only on the employer's own listing. */
  applicants?: number
  unreviewed?: number
  savedAt?: number
}

/**
 * Which posting the server picked out, and what it matched on.
 *
 * Reasons are codes rather than sentences so the badge can be translated;
 * see services/api/src/worx/matching.ts for what each one means.
 */
export interface MatchResult {
  id: string
  match: { score: number; reasons: ('category' | 'arrangement' | 'pay')[] }
}

export interface JobDetail extends JobCard {
  translation?: Translation | null
  description: string
  requirements: string | null
  startsOn: string | null
  employerAboutEn: string | null
  employerAboutEs: string | null
}

export interface Profile {
  headline: string | null
  summary: string | null
  photoId: string | null
  resumeId: string | null
  availability: string | null
  arrangements: string[]
  transport: string | null
  payMin: number | null
  payMax: number | null
  payUnit: string | null
  discoverable: boolean
  complete: boolean
  categories: string[]
  skills: string[]
  updatedAt: number | null
}

/**
 * A business, in whichever shape the server decided to hand back.
 *
 * The private half is optional in the TYPE for the same reason it is absent
 * from the response: a component holding this must not be able to assume a
 * phone number it was never given. See employers.ts `show`.
 */
export interface Employer {
  isDemo?: boolean
  id: string
  name: string
  categoryId: string | null
  logoId: string | null
  location: string | null
  aboutEn: string | null
  aboutEs: string | null
  tagline: string | null
  verified: boolean
  since: number
  /* Members only. */
  status?: Membership['status']
  contactName?: string | null
  phone?: string | null
  email?: string | null
  role?: 'owner' | 'member'
  members?: number
}

export interface Membership {
  isDemo?: boolean
  employerId: string
  role: 'owner' | 'member'
  name: string
  status: 'pending' | 'verified' | 'rejected' | 'suspended'
}

export interface Application {
  isDemo?: boolean
  id: string
  status: string
  createdAt: number
  updatedAt: number
  viewedAt: number | null
  jobId: string
  title: string
  jobStatus: string
  location: string | null
  payMin: number | null
  payMax: number | null
  payUnit: string | null
  employerId: string
  employerName: string
  employerLogoId: string | null
  threadId: string | null
}

export interface Applicant {
  id: string
  status: string
  /** Scored on the server from the snapshot — see worx/matching.ts. */
  match?: { score: number; reasons: ('category' | 'arrangement' | 'pay')[] }
  note: string | null
  availableFrom: string | null
  snapshot: string
  viewedAt: number | null
  createdAt: number
  updatedAt: number
  threadId: string | null
}

export interface Thread {
  isDemo?: boolean
  id: string
  subject: string
  lastMessageAt: number | null
  lastSnippet: string | null
  workerId: string
  employerId: string
  applicationId: string
  employerName: string
  employerLogoId: string | null
  workerName: string
  workerPhotoId: string | null
  /** The OTHER side's messages since this person last read. */
  unreadCount: number
  applicationStatus: string
  jobTitle: string
  readAt: number | null
  side: 'worker' | 'employer'
  unread: boolean
  blocked: boolean
}

export interface Message {
  id: string
  senderId: string
  side: 'worker' | 'employer'
  body: string
  relayedAs: string | null
  createdAt: number
  senderName: string
  /** The other person's words in this reader's language; absent for your own messages. */
  translated?: string | null
  from?: 'en' | 'es-PR'
}

/* ── Services for hire ────────────────────────────────────────────────────── */

export interface ServiceCard {
  titleTranslated?: string | null
  id: string
  title: string
  business: string | null
  categoryId: string | null
  area: string | null
  rateMin: number | null
  rateMax: number | null
  rateUnit: string | null
  status: 'draft' | 'published' | 'paused' | 'removed'
  publishedAt: number | null
  ownerId: string
  ownerName: string
  /** One id from shared/serviceColors.ts, or null for the default. */
  color: string | null
  /** An approved story AND preferred switched on for this ad. */
  preferred: boolean
  /** Present only on the owner's own list. */
  newRequests?: number
  requests?: number
}

export interface ServiceDetail extends ServiceCard {
  translation?: Translation | null
  description: string
  availability: string | null
  /** Withheld from signed-out viewers by the server. */
  phone?: string | null
  own: boolean
  saved: boolean
  requested: boolean
  removedReason?: string | null
  /** The owner-approved description, only while preferred is on. */
  story: string | null
}

export interface StoryState {
  cost: number
  balance: number
  aiReady: boolean
  /** Writing key AND speech-to-text are both configured. */
  voiceReady: boolean
  questions: ('what' | 'where' | 'like' | 'dislike' | 'other')[]
  answers: Record<string, string>
  draft: string | null
  story: string | null
}

export interface ServiceRequestSent {
  id: string
  serviceId: string
  serviceTitle: string
  message: string
  phone: string
  status: 'new' | 'handled' | 'cancelled'
  createdAt: number
  serviceStatus: string | null
  ownerName: string | null
}

export interface ServiceRequestReceived {
  translation?: Translation | null
  id: string
  requesterName: string
  message: string
  phone: string
  status: 'new' | 'handled' | 'cancelled'
  createdAt: number
}

/* ── Calls ────────────────────────────────────────────────────────────────── */

/**
 * Save the language on the account, so what other people write to this person is
 * translated into it. Fire-and-forget and keepalive: it is sent in the instant
 * before a reload, and a signed-out visitor's 401 is expected and ignored.
 */
export function saveLanguage(locale: 'en' | 'es-PR'): Promise<void> {
  return fetch('/api/worx/language', {
    method: 'POST', credentials: 'same-origin', keepalive: true,
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ locale }),
  }).then(() => undefined, () => undefined)
}

export const worx = {
  categories: () => get<{ categories: Category[] }>('/api/categories'),

  /* Worker */
  profile: () => get<{ profile: Profile | null }>('/api/me/profile'),
  saveProfile: (p: Partial<Profile>) => put<{ profile: Profile }>('/api/me/profile', p),

  feed: (params: { q?: string; category?: string; arrangement?: string; before?: number } = {}) => {
    const search = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) if (v) search.set(k, String(v))
    search.set('to', getLocale())
    const qs = search.toString()
    return get<{ jobs: JobCard[]; next: number | null; match: MatchResult | null; demoAccount: boolean }>(`/api/jobs${qs ? `?${qs}` : ''}`)
  },
  job: (id: string) => get<{ job: JobDetail; saved: boolean; applicationId: string | null }>(`/api/jobs/${id}?to=${getLocale()}`),
  savedJobs: () => get<{ jobs: JobCard[] }>('/api/me/saved'),
  save: (id: string, on: boolean) =>
    on ? put<{ ok: true }>(`/api/jobs/${id}/save`) : remove<{ ok: true }>(`/api/jobs/${id}/save`),

  apply: (jobId: string, body: { note?: string; availableFrom?: string }) =>
    post<{ id: string; status: string }>(`/api/jobs/${jobId}/apply`, body),
  applications: () => get<{ applications: Application[] }>('/api/applications'),
  withdraw: (id: string, note?: string) => post<{ ok: true }>(`/api/applications/${id}/withdraw`, { note }),

  /* Employer */
  myEmployers: () => get<{ employers: Membership[] }>('/api/me/employers'),
  employer: (id: string) => get<{ employer: Employer }>(`/api/employers/${id}`),
  registerEmployer: (body: Record<string, unknown>) =>
    post<{ employer: Membership }>('/api/employers', body),
  updateEmployer: (id: string, body: Record<string, unknown>) =>
    api<{ ok: true; reopened: boolean }>(`/api/employers/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  employerJobs: (id: string) => get<{ jobs: JobCard[] }>(`/api/employers/${id}/jobs`),
  dashboard: (id: string) =>
    get<{
      employer: { id: string; name: string; status: Membership['status']; role: 'owner' | 'member'; logoId: string | null }
      counts: { activeJobs: number; newApplicants: number; unreadMessages: number }
      jobs: { id: string; title: string; status: string; publishedAt: number | null; applicants: number; unreviewed: number }[]
      applicants: { id: string; status: string; viewedAt: number | null; createdAt: number; jobId: string; jobTitle: string; name: string; photoId: string | null }[]
    }>(`/api/employers/${id}/dashboard`),
  createJob: (employerId: string, body: Record<string, unknown>) =>
    post<{ id: string; status: string }>(`/api/employers/${employerId}/jobs`, body),
  updateJob: (id: string, body: Record<string, unknown>) =>
    api<{ ok: true }>(`/api/jobs/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  setJobStatus: (id: string, status: string) =>
    post<{ ok: true; status: string }>(`/api/jobs/${id}/status`, { status }),
  applicants: (jobId: string) =>
    get<{ job: { id: string; title: string }; applications: Applicant[] }>(`/api/jobs/${jobId}/applicants`),
  application: (id: string) =>
    get<{ application: Record<string, unknown>; events: Record<string, unknown>[]; viewerIsEmployer: boolean }>(
      `/api/applications/${id}`,
    ),
  setApplicationStatus: (id: string, status: string, note?: string) =>
    api<{ ok: true; status: string }>(`/api/applications/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status, note }),
    }),

  /* Mailbox */
  threads: () => get<{ threads: Thread[]; unread: number }>('/api/threads'),
  thread: (id: string) =>
    get<{ thread: Thread; messages: Message[]; blockedByMe: boolean; blockedByThem: boolean }>(`/api/threads/${id}`),
  sendMessage: (id: string, body: string) =>
    post<{ id: string; createdAt: number; relayed: boolean }>(`/api/threads/${id}/messages`, { body }),
  blockThread: (id: string, on: boolean) =>
    on ? put<{ ok: true }>(`/api/threads/${id}/block`) : remove<{ ok: true }>(`/api/threads/${id}/block`),
  report: (body: { subjectType: string; subjectId: string; reason: string; detail?: string }) =>
    post<{ id: string }>('/api/reports', body),

  /* Services for hire */
  services: (params: { q?: string; category?: string; before?: number } = {}) => {
    const search = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) if (v) search.set(k, String(v))
    search.set('to', getLocale())
    const qs = search.toString()
    return get<{ services: ServiceCard[]; next: number | null }>(`/api/services${qs ? `?${qs}` : ''}`)
  },
  service: (id: string) => get<{ service: ServiceDetail }>(`/api/services/${id}?to=${getLocale()}`),
  roles: () => get<{ roles: ('worker' | 'employer' | 'pro')[] }>('/api/worx/roles'),
  addRole: (role: 'worker' | 'employer' | 'pro') => post<{ roles: ('worker' | 'employer' | 'pro')[] }>('/api/worx/roles', { role }),
  myServices: () => get<{ services: (ServiceCard & Pick<ServiceDetail, 'description' | 'availability' | 'phone' | 'removedReason'>)[] }>('/api/me/services'),
  createService: (body: Record<string, unknown>) => post<{ id: string }>('/api/services', body),
  updateService: (id: string, body: Record<string, unknown>) =>
    api<{ ok: true }>(`/api/services/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  setServiceStatus: (id: string, status: 'published' | 'paused') =>
    post<{ ok: true; status: string }>(`/api/services/${id}/status`, { status }),
  deleteService: (id: string) => remove<{ ok: true }>(`/api/services/${id}`),
  savedServices: () => get<{ services: ServiceCard[] }>('/api/me/saved-services'),
  saveService: (id: string, on: boolean) =>
    on ? put<{ ok: true }>(`/api/services/${id}/save`) : remove<{ ok: true }>(`/api/services/${id}/save`),
  /** 404 when preferred is off for this ad: callers treat that as "not offered". */
  story: (id: string) => get<StoryState>(`/api/services/${id}/story`),
  draftStory: (id: string, answers: Record<string, string>, lang: string) =>
    post<{ draft: string; balance: number }>(`/api/services/${id}/story/draft`, { answers, lang }),
  /** The audio is the request body. It is transcribed and discarded server-side. */
  voiceStory: (id: string, audio: Blob, lang: string) =>
    api<{ draft: string; transcript: string; balance: number }>(`/api/services/${id}/story/voice?lang=${encodeURIComponent(lang)}`, {
      method: 'POST', body: audio, headers: { 'Content-Type': audio.type || 'audio/webm' },
    }),
  approveStory: (id: string, text?: string) =>
    post<{ ok: true; story: string }>(`/api/services/${id}/story/approve`, { text }),
  withdrawStory: (id: string) => remove<{ ok: true }>(`/api/services/${id}/story`),
  requestService: (id: string, body: { message: string; phone: string }) =>
    post<{ id: string }>(`/api/services/${id}/request`, body),
  serviceRequests: (id: string) => get<{ requests: ServiceRequestReceived[] }>(`/api/services/${id}/requests`),
  mySentRequests: () => get<{ requests: ServiceRequestSent[] }>('/api/me/service-requests'),
  setServiceRequestStatus: (id: string, status: 'new' | 'handled' | 'cancelled') =>
    api<{ ok: true; status: string }>(`/api/service-requests/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),

  /* Admin */
  employerQueue: (status = 'pending') =>
    get<{ employers: Record<string, unknown>[] }>(`/api/admin/employers?status=${status}`),
  decideEmployer: (id: string, status: string, decision?: string) =>
    post<{ ok: true }>(`/api/admin/employers/${id}/decision`, { status, decision }),
  removeJob: (id: string, reason: string) => post<{ ok: true }>(`/api/admin/jobs/${id}/remove`, { reason }),
  reports: (status = 'open') => get<{ reports: Record<string, unknown>[] }>(`/api/admin/reports?status=${status}`),
  resolveReport: (id: string, status: string, resolution?: string) =>
    post<{ ok: true }>(`/api/admin/reports/${id}/resolve`, { status, resolution }),
  migrations: () =>
    get<{
      migrations: { name: string; sha: string; statements: number; state: string; appliedAt: string | null }[]
      pending: number
      orphans: number
      environment: string
      canApply: boolean
    }>('/api/admin/migrations'),
  applyMigration: (name: string, confirm: string, markOnly = false) =>
    post<{ ok: true; statements: number }>('/api/admin/migrations/apply', { name, confirm, markOnly }),

  /* The command line */
  voice: (input: string) =>
    post<{
      ok: boolean
      outcome: 'ok' | 'refused' | 'escalated' | 'error'
      message: string
      intent: string
      ticketId?: string
    }>('/api/voice', { input }),
}
